import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useEffect } from "react";

// Pages
import HomePage from "@/pages/HomePage";
import CarDetailPage from "@/pages/CarDetailPage";
import CarsPage from "@/pages/CarsPage";
import SoldCarsPage from "@/pages/SoldCarsPage";
import WeFindPage from "@/pages/WeFindPage";
import ComparePage from "@/pages/ComparePage";
import NewsletterUnsubscribePage from "@/pages/NewsletterUnsubscribePage";
import LegalPage from "@/pages/LegalPage";
import TeamPage from "@/pages/TeamPage";
import BlogPage from "@/pages/BlogPage";
import BlogDetailPage from "@/pages/BlogDetailPage";
import ContractSignPage from "@/pages/ContractSignPage";
import TestDrivePage from "@/pages/TestDrivePage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCars from "@/pages/admin/AdminCars";
import AdminCarForm from "@/pages/admin/AdminCarForm";
import AdminInquiries from "@/pages/admin/AdminInquiries";
import AdminCarRequests from "@/pages/admin/AdminCarRequests";
import AdminInquiryDetail from "@/pages/admin/AdminInquiryDetail";
import AdminInquiriesArchive from "@/pages/admin/AdminInquiriesArchive";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminPassword from "@/pages/admin/AdminPassword";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerForm from "@/pages/admin/AdminCustomerForm";
import AdminCustomerDetail from "@/pages/admin/AdminCustomerDetail";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminContracts from "@/pages/admin/AdminContracts";
import AdminTestDrive from "@/pages/admin/AdminTestDrive";
import AdminTestDriveArchive from "@/pages/admin/AdminTestDriveArchive";
import AdminSupport from "@/pages/admin/AdminSupport";
import ForgotPassword from "@/pages/admin/ForgotPassword";
import ResetPassword from "@/pages/admin/ResetPassword";
import FavoritesPage from "@/pages/FavoritesPage";
import InquiryReplyPage from "@/pages/InquiryReplyPage";
import AdminModuleSettings from "@/pages/admin/AdminModuleSettings";

// Context Providers
import { AuthProvider } from "@/context/AuthContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { SiteSettingsProvider } from "@/context/SiteSettingsContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { AdminLanguageProvider } from "@/context/AdminLanguageContext";
import DynamicMeta from "@/components/DynamicMeta";
import CookieBanner from "@/components/CookieBanner";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function App() {
  return (
    <div className="noise-overlay">
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <FavoritesProvider>
              <SiteSettingsProvider>
                <BrowserRouter>
                  <ScrollToTop />
                  <DynamicMeta />
                  <CookieBanner />
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<HomePage />} />
                    <Route path="/cars" element={<CarsPage />} />
                    <Route path="/cars/:id" element={<CarDetailPage />} />
                    <Route path="/verkaufte-fahrzeuge" element={<SoldCarsPage />} />
                    <Route path="/sold-cars" element={<SoldCarsPage />} />
                    <Route path="/wir-finden" element={<WeFindPage />} />
                    <Route path="/we-find" element={<WeFindPage />} />
                    <Route path="/favorites" element={<FavoritesPage />} />
                    <Route path="/compare" element={<ComparePage />} />
                    <Route path="/team" element={<TeamPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogDetailPage />} />
                    <Route path="/test-drive" element={<TestDrivePage />} />
                    <Route path="/contract/:contractId/sign" element={<ContractSignPage />} />
                    <Route path="/contract/:contractId" element={<ContractSignPage />} />
                    <Route path="/inquiry/:id/reply" element={<InquiryReplyPage />} />
                    <Route path="/newsletter/unsubscribe/:id" element={<NewsletterUnsubscribePage />} />
                    
                    {/* Legal Pages */}
                    <Route path="/datenschutz" element={<LegalPage />} />
                    <Route path="/agb" element={<LegalPage />} />
                    <Route path="/impressum" element={<LegalPage />} />
                    <Route path="/vorbehalten" element={<LegalPage />} />
                    <Route path="/legal/:page" element={<LegalPage />} />
                    
                    {/* Admin Routes */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin/forgot-password" element={<ForgotPassword />} />
                    <Route path="/admin/reset-password" element={<ResetPassword />} />
                    <Route path="/admin" element={<AdminLayout />}>
                      <Route index element={<AdminDashboard />} />
                      <Route path="cars" element={<AdminCars />} />
                      <Route path="cars/new" element={<AdminCarForm />} />
                      <Route path="cars/:id/edit" element={<AdminCarForm />} />
                      <Route path="customers" element={<AdminCustomers />} />
                      <Route path="customers/new" element={<AdminCustomerForm />} />
                      <Route path="customers/:id" element={<AdminCustomerDetail />} />
                      <Route path="customers/:id/edit" element={<AdminCustomerForm />} />
                      <Route path="analytics" element={<AdminAnalytics />} />
                      <Route path="inquiries" element={<AdminInquiries />} />
                      <Route path="car-requests" element={<AdminCarRequests />} />
                      <Route path="inquiries/archive" element={<AdminInquiriesArchive />} />
                      <Route path="inquiries/:id" element={<AdminInquiryDetail />} />
                      <Route path="media" element={<AdminMedia />} />
                      <Route path="settings" element={<AdminSettings />} />
                      <Route path="settings/module/:moduleId" element={<AdminModuleSettings />} />
                      <Route path="blog" element={<AdminBlog />} />
                      <Route path="contracts" element={<AdminContracts />} />
                      <Route path="test-drive" element={<AdminTestDrive />} />
                      <Route path="test-drive/archive" element={<AdminTestDriveArchive />} />
                      <Route path="support" element={<AdminSupport />} />
                      <Route path="newsletter" element={<AdminNewsletter />} />
                      <Route path="password" element={<AdminPassword />} />
                    </Route>
                  </Routes>
                </BrowserRouter>
                <Toaster position="top-right" richColors />
              </SiteSettingsProvider>
            </FavoritesProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
